import { wishList } from './wishlist';

// Constants
const MOBILE_BREAKPOINT = 768;
const CLICK_DELAY = 300;

// State
const state = {
  isMobile: window.innerWidth <= MOBILE_BREAKPOINT,
  activeButtonParent: null,
  isAddToCartTriggered: false,
  isCartOpenTriggered: false
};

// DOM Selectors
const selectors = {
  sizeContainers: '[id^="sizes-by-"]',
  productButtons: '.main-content.main-content-collection:not(.original) .btn-add-to-cart, .custom-collection-grid .btn-add-to-cart',
  desktopFilters: '#FacetsWrapperDesktop .dropdown',
  mobileFilters: '#FacetFiltersFormMobile',
  header: 'header.site-header.page-element',
  desktopSortForm: '#FacetFiltersForm',
  mobileSortContainer: '.mobile-facets__sort'
};

/**
 * Hides all size containers and resets hover button visibility.
 */
const hideSizeContainers = () => {
  const containers = document.querySelectorAll(selectors.sizeContainers);
  containers.forEach(container => {
    container.classList.remove('open-sizes');
    container.querySelector('ul').classList.remove('open-sizes');

    const hoverButton = container.previousElementSibling;
    if (hoverButton?.classList.contains('hover-buttons')) {
      hoverButton.style.display = '';
    }
  });
};

/**
 * Handles the button click action by revealing the size options
 * for the specified product and hiding the clicked button.
 */
const handleButtonAction = (event, button) => {
  event.preventDefault();

  const variantId = event.currentTarget.getAttribute('data-product-id');
  const sizeElement = document.querySelector(`[id*="sizes-by-${variantId}"]`);
  const elements = sizeElement.querySelector('ul');
  state.activeButtonParent = button.parentElement;

  if (sizeElement && elements && elements.childElementCount > 0) {
    hideSizeContainers();
    sizeElement.classList.add('open-sizes');
    sizeElement.setAttribute('tabindex', '-1');
    sizeElement.focus();

    if (state.isMobile) {
      document.body.classList.add('overlay');

      setTimeout(() => {
        const handleClickOutside = (event) => {
          if (!sizeElement.contains(event.relatedTarget)) {
            if(!state.isAddToCartTriggered && !state.isCartOpenTriggered) {
              if (state.activeButtonParent) state.activeButtonParent.style.display = '';
              sizeElement.classList.remove('open-sizes');
              sizeElement.querySelector('ul').classList.remove('open-sizes');
              sizeElement.removeAttribute('tabindex');
              document.body.classList.remove('overlay');
              document.removeEventListener('click', handleClickOutside);
            }
          }
        };

        document.addEventListener('click', handleClickOutside);
      }, CLICK_DELAY);
    }

    sizeElement.addEventListener('mouseout', (e) => {
      if (!sizeElement.contains(e.relatedTarget)) {
        if (state.activeButtonParent) state.activeButtonParent.style.display = '';
        sizeElement.classList.remove('open-sizes');
        sizeElement.querySelector('ul').classList.remove('open-sizes');
        sizeElement.removeAttribute('tabindex');
        if (state.isMobile) document.body.classList.remove('overlay');
      }
    });

    if (state.activeButtonParent) {
      state.activeButtonParent.style.display = 'none';
    }
  }
};

/**
 * Attaches click event listeners to all "Add to Cart" buttons
 */
const attachButtonClickListeners = () => {
  const buttons = document.querySelectorAll(selectors.productButtons);
  buttons.forEach(button => {
    button.addEventListener('click', (event) => handleButtonAction(event, button));
  });
};

/**
 * Attaches click event listener to a single button
 */
const attachButtonClickListener = (element) => {
  const elementButton = element.querySelector('.btn-add-to-cart');
  elementButton?.addEventListener('click', (event) => handleButtonAction(event, elementButton));
};

/**
 * Updates the mini cart and dispatches a custom event after successful addition
 */
async function updateMiniCart(cartData) {
  try {
    await Promise.all([
      theme.miniCart.updateElements(),
      theme.miniCart.generateCart()
    ]);

    const addToCartEvent = new CustomEvent('pdm-add-to-cart', {
      detail: { items: "sended", data: cartData }
    });
    document.dispatchEvent(addToCartEvent);
  } catch (error) {
    console.error('Error updating mini cart:', error);
  }
}

/**
 * Sends a request to add a product to the cart
 */
async function addProductToCart(productId, quantity = 1) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: productId, quantity })
    });

    const data = await response.json();
    await updateMiniCart(data);
  } catch (error) {
    console.error('Error adding product to cart:', error);
  }
}

/**
 * Handles the product selection event for radio buttons
 */
async function handleProductSelection(event) {
  const productId = event.target.value;
  await addProductToCart(productId, 1);
}

/**
 * Handles the add to cart event for buttons without size options
 */
async function handleAddToCart(event) {
  const productId = event.target.getAttribute('pid');
  await addProductToCart(productId, 1);
}

/**
 * Initializes cart events for a container
 */
function initializeCartEvents(container) {
  container.querySelectorAll('.submit-radio')
    .forEach(radio => radio.addEventListener('change', handleProductSelection));

  container.querySelectorAll('.no-size-add')
    .forEach(button => button.addEventListener('click', handleAddToCart));
}

/**
 * Closes the sizes selector and resets state
 */
const closeSizesSelector = () => {
  const openSizesElements = document.querySelectorAll('.open-sizes');
  openSizesElements.forEach(element => element.classList.remove('open-sizes'));

  if (state.activeButtonParent) {
    state.activeButtonParent.style.display = '';
  }

  document.body.classList.remove('overlay');
  state.isCartOpenTriggered = false;
  state.isAddToCartTriggered = false;
};

/**
 * Initializes desktop categories selector
 */
const categoriesSelector = () => {
  const filters = document.querySelector(selectors.desktopFilters);
  if (!filters) return;

  const categories = filters.querySelector('.dropdown-btn');
  if (!categories) return;

  const content = filters.querySelector('#ul_list_categories');
  if (!content) return;

  categories.addEventListener('click', () => {
    content.style.display = content.style.display === 'inline-flex' ? 'none' : 'inline-flex';
    content.addEventListener('mouseleave', () => {
      content.style.display = 'none';
    });
  });

  categories.addEventListener('mouseout', (event) => {
    if (!content.contains(event.relatedTarget)) {
      content.style.display = 'none';
    }
  });
};

/**
  * Updates category links with current URL parameters
*/
const updateURLCategoryLink = (content) => {
  if( !content) return;

  const categories = content.querySelectorAll('a');

  const currentUrl = new URL(window.location.href);
  const params = new URLSearchParams(currentUrl.search);
  params.delete('page');
  const queryString = params.toString();

  // Update category links
  categories.forEach(category => {
    const categoryUrl = new URL(category.href);
    categoryUrl.search = queryString;
    category.href = categoryUrl.toString();
  });
}

/**
 * Updates category links with current URL parameters
 */
const updateURLCategoriesSelector = () => {
  const filters = document.querySelector(selectors.desktopFilters);
  if (!filters) return;

  const content = filters.querySelector('#ul_list_categories');
  if (!content) return;

  updateURLCategoryLink(content);

};

/**
 * Initializes mobile categories selector
 */
const categoriesMobileSelector = () => {
  const filters = document.querySelector(selectors.mobileFilters);
  if (!filters) return;
  const categoriesBtn = filters.querySelector('.categories-dropdown__dropdown-btn');
  if (!categoriesBtn) return;

  categoriesBtn.addEventListener('click', () => {

    const categoriesContainer = filters.querySelector('.categories-dropdown');
    const sortByContainer = filters.querySelector('.mobile-facets__sort .dropdown__sort-by');

    if (categoriesContainer && categoriesContainer.classList.contains('open')){
      categoriesContainer.classList.remove('open');
    }else {
      categoriesContainer.classList.add('open');
    }

    if (sortByContainer) sortByContainer.classList.remove('open');
  });
};

/**
  * Updates category links with current URL parameters
*/
const updateURLCategoriesMobileSelector = () => {
  const filters = document.querySelector(selectors.mobileFilters);
  if (!filters) return;

  const content = filters.querySelector('#ul_list_categories');
  if (!content) return;

  updateURLCategoryLink(content);
};

/**
 * Initializes sort by functionality for mobile view
 */
const sortByMobileSelector = () => {
  const filters = document.querySelector(selectors.mobileFilters);
  if (!filters) return;

  const button = filters.querySelector('.mobile-facets__sort .sort_by-container');
  if (!button) return;

  button.addEventListener('click', () => {
    const sortByContainer = filters.querySelector('.mobile-facets__sort .dropdown__sort-by');
    const categoriesContainer = filters.querySelector('.categories-dropdown');

    if (sortByContainer) sortByContainer.classList.toggle('open');
    if (categoriesContainer) categoriesContainer.classList.remove('open');
  });

  const inputElements = filters.querySelectorAll('.dropdown__sort-by .dropdown-content input[type="radio"]');
  inputElements.forEach(input => {
    input.addEventListener('change', () => {
      filters.submit();
    });
  });
};

/**
 * Initializes sort by functionality for desktop view
 */
const sortBySelector = () => {
  const filters = document.getElementById('FacetFiltersForm');
  if (!filters) return;

  const sortBy = filters.querySelector('.select-trigger');
  const svgSortBy = filters.querySelector('.facet-filters__field > svg');
  const selectElement = filters.querySelector('.facet-filters.sorting.caption .facet-filters__field #SortBy');
  const optionsContainer = filters.querySelector('.custom-options');
  const sortByContainer = filters.querySelector('.select__sort-by');

  if (!sortBy || !svgSortBy || !selectElement || !optionsContainer || !sortByContainer) return;

  // Handle click events for sort trigger and svg icon
  const handleSortToggle = () => {
    sortByContainer.classList.toggle('open');
  };

  svgSortBy.addEventListener('click', handleSortToggle);
  sortBy.addEventListener('click', handleSortToggle);

  // Handle mouseout for the entire sort container
  const handleMouseOut = (event) => {
    const isOverSortBy = sortBy.contains(event.relatedTarget);
    const isOverSvg = svgSortBy.contains(event.relatedTarget);
    const isOverOptions = optionsContainer.contains(event.relatedTarget);

    if (!isOverSortBy && !isOverSvg && !isOverOptions) {
      sortByContainer.classList.remove('open');
    }
  };

  sortBy.addEventListener('mouseout', handleMouseOut);
  svgSortBy.addEventListener('mouseout', handleMouseOut);

  // Handle mouseleave for options container
  optionsContainer.addEventListener('mouseleave', (event) => {
    const isOverSortBy = sortBy.contains(event.relatedTarget);
    const isOverSvg = svgSortBy.contains(event.relatedTarget);

    if (!isOverSortBy && !isOverSvg) {
      sortByContainer.classList.remove('open');
    }
  });

  // Handle option selection
  const options = filters.querySelectorAll('.custom-option');
  if (options.length > 0) {
    options.forEach(option => {
      option.addEventListener('click', function() {
        const value = this.getAttribute('data-value');
        const text = this.textContent;

        // Update sort trigger text
        sortBy.textContent = text;
        sortByContainer.classList.remove('open');

        // Update selected state for options
        options.forEach(opt => opt.classList.remove('selected'));
        this.classList.add('selected');

        // Update select element options
        const selectOptions = selectElement.querySelectorAll('option');
        selectOptions.forEach(selectOption => {
          selectOption.removeAttribute('selected');
          if (selectOption.value === value) {
            selectOption.setAttribute('selected', 'selected');
          }
        });

        // Submit the form
        filters.submit();
      });
    });
  }
};

/**
 * Initializes mobile modal behavior
 */
const modalButtonsBehaviorMobileSelector = () => {
  const filters = document.querySelector('.mobile-facets__wrapper .mobile-facets__open-wrapper');
  if (!filters) return;

  const modalOpenBtn = filters.querySelector('.mobile-facets__open');
  const modalCloseBtn = filters.querySelector('.mobile-facets__close');
  const modalFooter = document.querySelector('.mobile-facets__footer');
  const headerElement = document.querySelector(selectors.header);

  const toggleHeader = (show) => {
    if (headerElement) headerElement.style.display = show ? "" : "none";
  };

  if (modalOpenBtn) modalOpenBtn.addEventListener('click', () => toggleHeader(false));
  if (modalCloseBtn) modalCloseBtn.addEventListener('click', () => toggleHeader(true));

  if (modalFooter) {
    modalFooter.querySelector('.mobile-facets__clear-wrapper')
      ?.addEventListener('click', () => toggleHeader(true));

    modalFooter.querySelector('.button.button--primary')
      ?.addEventListener('click', () => toggleHeader(true));
  }
};

/**
 * Initializes size filter listeners
 */
const filterSizeListener = () => {
  const templateFilters = document.querySelector('.pdm-collection-filters');
  if (!templateFilters) return;

  const filterElement = templateFilters.querySelector('#FacetFiltersFormMobile #FacetsWrapperMobile');
  if (!filterElement) return;

  const elementOption = filterElement.querySelector('[id*="Details-Mobile-filter.v.option.size"]');
  if (!elementOption) return;

  const filterDivElement = elementOption.querySelector('div.mobile-facets__details .mobile-facets__summary');
  if (!filterDivElement) return;

  const innerDiv = filterDivElement.querySelector('div');
  if (!innerDiv) return;
  if (innerDiv.dataset.hasClickListener) return;

  innerDiv.dataset.hasClickListener = 'true';
  innerDiv.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const facetList = innerDiv.nextElementSibling;

    if (!facetList) return;
    if (facetList.classList.contains('is-visible')) {
      facetList.classList.remove('is-visible');
    } else {
      facetList.classList.add('is-visible');
    }
  });
};

/**
 * Initializes all product filters functionality
 */
const initializePdmFiltersProductCards = () => {
  attachButtonClickListeners();

  if (state.isMobile) {
    updateURLCategoriesMobileSelector();
    categoriesMobileSelector();
    sortByMobileSelector();
    modalButtonsBehaviorMobileSelector();
    filterSizeListener();
  } else {
    updateURLCategoriesSelector();
    categoriesSelector();
    sortBySelector();
  }
};

// Event Listeners
document.addEventListener('newitems', (event) => {
  const { items } = event.detail;
  if (items?.length > 0) {
    items.forEach(item => {
      initializeCartEvents(item);
      attachButtonClickListener(item);
    });
  }
});

document.addEventListener('pdm-add-to-cart', () => {
  state.isAddToCartTriggered = true;
  if (state.isCartOpenTriggered) {
    closeSizesSelector();
  }
});

document.addEventListener('pdm-cart-toggle', (event) => {
  state.isCartOpenTriggered = event.detail.isCartOpen;
  if (state.isAddToCartTriggered && state.isCartOpenTriggered) {
    closeSizesSelector();
  }
});

document.addEventListener('pdm-facets-filters', () => {
  attachButtonClickListeners();
  wishList();

  if (state.isMobile) {
    const headerElement = document.querySelector(selectors.header);
    if (headerElement) headerElement.style.display = "";
    updateURLCategoriesMobileSelector();
    sortByMobileSelector();
    modalButtonsBehaviorMobileSelector();
    filterSizeListener();
  } else {
    sortBySelector();
    updateURLCategoriesSelector();
  }

  theme.addToCartOnPLP.load();
});

export default initializePdmFiltersProductCards;
